import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { useCallback } from "react";
import { adminUploadImage } from "@/lib/api";
import { toast } from "sonner";

/**
 * Minimal, SEO-clean rich text editor.
 * Only enabled features: H2/H3/H4, paragraphs, bold, italic, bullet/ordered lists,
 * links, images, blockquote, horizontal rule, undo/redo.
 * Produces clean semantic HTML with no inline styles.
 */
export default function RichTextEditor({ value, onChange }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3, 4] },
        // Explicitly disable features not requested to keep output clean.
        codeBlock: false,
        code: false,
        strike: false,
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
      }),
      Image.configure({ inline: false, allowBase64: false }),
    ],
    content: value || "",
    onUpdate: ({ editor: ed }) => onChange && onChange(ed.getHTML()),
    editorProps: {
      attributes: { class: "article-editor-body" },
      transformPastedHTML: (html) =>
        // Strip inline styles, spans, font tags, and Word/Docs artefacts.
        html
          .replace(/<span[^>]*>/gi, "")
          .replace(/<\/span>/gi, "")
          .replace(/<font[^>]*>/gi, "")
          .replace(/<\/font>/gi, "")
          .replace(/ style="[^"]*"/gi, "")
          .replace(/ class="[^"]*"/gi, "")
          .replace(/<o:p>[\s\S]*?<\/o:p>/gi, ""),
    },
  });

  const setLink = useCallback(() => {
    if (!editor) return;
    const previous = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL", previous);
    if (url === null) return;
    if (url === "") { editor.chain().focus().unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  const insertImage = useCallback(async () => {
    if (!editor) return;
    const input = document.createElement("input");
    input.type = "file"; input.accept = "image/*";
    input.onchange = async () => {
      const f = input.files?.[0];
      if (!f) return;
      try {
        const { url } = await adminUploadImage(f);
        const abs = url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
        editor.chain().focus().setImage({ src: abs, alt: f.name.replace(/\.[^.]+$/, "") }).run();
      } catch {
        toast.error("Image upload failed");
      }
    };
    input.click();
  }, [editor]);

  if (!editor) return null;
  const btn = (active, onClick, label, testid) => (
    <button
      type="button"
      className={`article-tb-btn ${active ? "active" : ""}`.trim()}
      onClick={onClick}
      data-testid={testid}
    >{label}</button>
  );

  return (
    <div className="article-editor" data-testid="article-editor">
      <div className="article-editor-toolbar" role="toolbar" aria-label="Formatting">
        {btn(editor.isActive("heading", { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), "H2", "tb-h2")}
        {btn(editor.isActive("heading", { level: 3 }), () => editor.chain().focus().toggleHeading({ level: 3 }).run(), "H3", "tb-h3")}
        {btn(editor.isActive("heading", { level: 4 }), () => editor.chain().focus().toggleHeading({ level: 4 }).run(), "H4", "tb-h4")}
        {btn(editor.isActive("paragraph"), () => editor.chain().focus().setParagraph().run(), "P", "tb-p")}
        <span className="article-tb-sep" />
        {btn(editor.isActive("bold"), () => editor.chain().focus().toggleBold().run(), <strong>B</strong>, "tb-bold")}
        {btn(editor.isActive("italic"), () => editor.chain().focus().toggleItalic().run(), <em>I</em>, "tb-italic")}
        <span className="article-tb-sep" />
        {btn(editor.isActive("bulletList"), () => editor.chain().focus().toggleBulletList().run(), "• List", "tb-ul")}
        {btn(editor.isActive("orderedList"), () => editor.chain().focus().toggleOrderedList().run(), "1. List", "tb-ol")}
        {btn(editor.isActive("blockquote"), () => editor.chain().focus().toggleBlockquote().run(), "❝", "tb-quote")}
        {btn(false, () => editor.chain().focus().setHorizontalRule().run(), "―", "tb-hr")}
        <span className="article-tb-sep" />
        {btn(editor.isActive("link"), setLink, "Link", "tb-link")}
        {btn(false, insertImage, "Image", "tb-img")}
        <span className="article-tb-sep" />
        {btn(false, () => editor.chain().focus().undo().run(), "↶", "tb-undo")}
        {btn(false, () => editor.chain().focus().redo().run(), "↷", "tb-redo")}
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
